import React from 'react';
import { RsvpForm } from '@/src/modules/rsvp';
import { registry } from '../registry';
import type { InvitationData, TemplateMetadata } from '../types';
import { parseFlexibleDate } from '@/src/infrastructure/lib/weddingDataHelper';

const META: TemplateMetadata = {
  id: 'seed-baby-shower-premium',
  name: 'Baby Shower Bloom',
  occasionType: 'BABY_SHOWER',
  description: 'Soft pastel pink and mint with a gentle whimsical feel.',
  isPremium: false,
  tags: ['pastel', 'soft', 'cute', 'baby'],
  colorScheme: { primary: '#ec4899', accent: '#2dd4bf', bg: '#fdf2f8' },
};

function fmt(iso: string | undefined): string {
  if (!iso) return '';
  const d = parseFlexibleDate(iso);
  if (!d) return iso;
  try {
    return new Intl.DateTimeFormat('en-IN', {
      weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: true,
    }).format(d);
  } catch {
    return iso;
  }
}

export function BabyShowerBloom({ data }: { data: InvitationData }) {
  const [hero, ...gallery] = data.mediaUrls ?? [];
  const invitationId = data.invitationId ?? data.id ?? null;
  const isLive = data.status === 'PUBLISHED' || data.status === 'PROCESSING';

  return (
    <div className="min-h-screen bg-pink-50 font-sans flex items-center justify-center p-8">
      <div className="w-full max-w-2xl bg-white rounded-3xl shadow-xl overflow-hidden border border-pink-100">
        {/* petal header */}
        <div className="bg-gradient-to-r from-pink-200 via-rose-100 to-teal-100 px-10 py-10 text-center">
          <div className="text-5xl mb-3">🌸</div>
          <p className="text-pink-400 text-xs uppercase tracking-widest font-semibold mb-2">
            A little one is on the way
          </p>
          <h1 className="text-4xl font-bold text-pink-800 leading-tight">
            {data.eventName}
          </h1>
          <p className="text-pink-500 text-sm mt-2">
            Hosted with love by{' '}
            <span className="font-semibold">{data.hostName}</span>
          </p>
        </div>

        {/* hero */}
        {hero && (
          <div className="w-full aspect-video overflow-hidden">
            <img src={hero} alt="" className="w-full h-full object-cover" />
          </div>
        )}

        <div className="px-10 py-10 text-center">
          {/* info */}
          <div className="grid grid-cols-2 gap-4 mb-8">
            <div className="bg-pink-50 rounded-2xl p-5 border border-pink-100 text-left">
              <p className="text-xs text-pink-400 uppercase tracking-widest mb-1">🗓 Date &amp; Time</p>
              <p className="text-pink-800 font-semibold text-sm leading-snug">
                {fmt(data.eventDate)}
              </p>
            </div>
            <div className="bg-teal-50 rounded-2xl p-5 border border-teal-100 text-left">
              <p className="text-xs text-teal-500 uppercase tracking-widest mb-1">📍 Location</p>
              <p className="text-teal-800 font-semibold text-sm leading-snug">
                {data.eventVenue}
              </p>
            </div>
          </div>

          {/* divider */}
          <div className="flex items-center gap-3 my-6">
            <div className="flex-1 h-px bg-pink-100" />
            <span className="text-pink-300 text-lg">✿</span>
            <div className="flex-1 h-px bg-pink-100" />
          </div>

          {/* message */}
          {data.customMessage && (
            <p className="text-pink-700 italic text-base leading-relaxed mb-8 px-4">
              "{data.customMessage}"
            </p>
          )}

          {/* gallery */}
          {gallery.length > 0 && (
            <div className="grid grid-cols-3 gap-3 mb-8">
              {gallery.slice(0, 3).map((url, i) => (
                <div key={i} className="aspect-square rounded-2xl overflow-hidden border-2 border-pink-100">
                  <img src={url} alt="" className="w-full h-full object-cover" />
                </div>
              ))}
            </div>
          )}

          {/* RSVP */}
          <div style={{ background: 'rgba(236,72,153,0.85)', borderRadius: 20, padding: '24px 20px' }}>
            <RsvpForm invitationId={invitationId} isLive={isLive} externalLink={data.rsvpLink || null} />
          </div>
        </div>

        <div className="bg-gradient-to-r from-teal-100 via-rose-100 to-pink-200 h-3" />
      </div>
    </div>
  );
}

registry.register({ metadata: META, component: BabyShowerBloom });
